/**
 * Jack & Jones — quick buy (snippets/jj-quick-buy.liquid): the sizes and the add button of the
 * listing cards (jj-collection-grid.js), of the search cards (snippets/jj-search-card.liquid) and of
 * the product carousel cards with «Compra rápida» on (snippets/jj-product-card.liquid).
 * It reads /cart/add.js and shows what the API says right on the button (rule A3).
 * On mobile the bag icon of a card ([data-jj-quick-open]) copies its quick buy into the section's
 * sheet ([data-jj-quick-sheet], Figma «Compra rápida mobile» 310:1363).
 *
 * The listener is delegated on the document, so cards appended by the infinite scroll, the mobile
 * sheet and the predictive search results work without re-binding.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const desktopQuery = window.matchMedia('(min-width: 750px)');

/**
 * Text the button shows at rest: AGOTADO, AGREGAR AL CARRITO (size chosen or no sizes) or SELECCIONAR TALLE.
 * @param {HTMLElement} quick - [data-jj-quick]
 */
function restingLabel(quick) {
  const button = quick.querySelector('[data-jj-quick-add]');
  if (button?.hasAttribute('disabled')) return quick.dataset.labelSoldOut || '';
  const ready = quick.dataset.variantId || (button instanceof HTMLElement && button.dataset.variantId);
  return (ready ? quick.dataset.labelAdd : quick.dataset.labelSelect) || '';
}

/**
 * @param {HTMLElement} quick - [data-jj-quick]
 * @param {string} text
 * @param {number} [resetAfter] - ms after which the label goes back to its resting text
 */
function setQuickLabel(quick, text, resetAfter) {
  const label = quick.querySelector('[data-jj-quick-label]');
  if (!label) return;
  label.textContent = text;
  window.clearTimeout(Number(quick.dataset.jjTimer));
  if (resetAfter) {
    quick.dataset.jjTimer = String(
      window.setTimeout(() => {
        label.textContent = restingLabel(quick);
        quick.classList.remove('is-warning');
      }, resetAfter)
    );
  }
}

/** @param {HTMLElement} sizeButton */
function selectSize(sizeButton) {
  const quick = sizeButton.closest('[data-jj-quick]');
  if (!(quick instanceof HTMLElement)) return;
  quick.querySelectorAll('[data-jj-size]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button === sizeButton));
  });
  quick.dataset.variantId = sizeButton.dataset.jjSize || '';
  quick.classList.remove('is-warning');
  setQuickLabel(quick, restingLabel(quick));
}

/**
 * Tells the header cart icon (Horizon's cart-icon listens to cart lines updates) the new count.
 * @param {{ resolve: Function } | null} deferred
 * @param {any} cart - /cart.js
 * @param {boolean} didError
 */
function resolveCartEvent(deferred, cart, didError) {
  if (!deferred) return;
  try {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'jj-plp-card', didError },
    });
  } catch (error) {
    console.warn('[jj-quick-buy] cart event', error);
  }
}

/** @param {HTMLElement} button - [data-jj-quick-add] */
async function quickAdd(button) {
  const quick = button.closest('[data-jj-quick]');
  if (!(quick instanceof HTMLElement) || quick.dataset.jjBusy) return;

  const variantId = button.dataset.variantId || quick.dataset.variantId;
  if (!variantId) {
    // «ELEGÍ UN TALLE» on the same line: nothing else moves (rule A5)
    quick.classList.add('is-warning');
    setQuickLabel(quick, quick.dataset.labelChoose || '', 2000);
    return;
  }

  quick.dataset.jjBusy = 'true';
  button.setAttribute('aria-busy', 'true');
  setQuickLabel(quick, quick.dataset.labelAdding || '');

  /** @type {any} */
  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );
  } catch (error) {
    deferred = null;
  }

  const routes = window.Theme?.routes || {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    const data = await response.json();

    if (response.ok) {
      setQuickLabel(quick, quick.dataset.labelAdded || '', 2500);
    } else {
      // e.g. «Solo hay 2 unidades» / «El producto está agotado»: said where it was asked (rule A3)
      setQuickLabel(quick, data.description || data.message || quick.dataset.labelError || '', 4000);
    }

    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    resolveCartEvent(deferred, cart, !response.ok);
  } catch (error) {
    deferred?.reject(error);
    setQuickLabel(quick, quick.dataset.labelError || '', 4000);
  } finally {
    delete quick.dataset.jjBusy;
    button.removeAttribute('aria-busy');
  }
}

/* ----------------------------------------------------------------------------------------------
 * Mobile sheet
 * ------------------------------------------------------------------------------------------- */

/** @type {HTMLElement | null} */
let sheetTrigger = null;

/** @param {HTMLElement} trigger - the bag icon of a card */
function openSheet(trigger) {
  const card = trigger.closest('.jj-plp-card, .jj-card');
  const root = trigger.closest('.shopify-section');
  const quick = card?.querySelector('[data-jj-quick]');
  const sheet = root?.querySelector('[data-jj-quick-sheet]');
  const body = sheet?.querySelector('[data-jj-quick-sheet-body]');
  if (!(sheet instanceof HTMLDialogElement) || !body || !(quick instanceof HTMLElement)) return;

  const copy = /** @type {HTMLElement} */ (quick.cloneNode(true));
  delete copy.dataset.variantId;
  copy.querySelectorAll('[data-jj-size]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
  body.replaceChildren(copy);

  const title = sheet.querySelector('[data-jj-quick-sheet-title]');
  if (title) title.textContent = quick.dataset.productTitle || '';

  sheetTrigger = trigger;
  sheet.showModal();
  lockScroll(sheet);
}

/** @param {Event} event */
function onDialogClose(event) {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-jj-quick-sheet]')) return;
  unlockScroll(dialog);
  if (sheetTrigger?.isConnected) sheetTrigger.focus({ preventScroll: true });
  sheetTrigger = null;
}

function onBreakpointChange() {
  document.querySelectorAll('[data-jj-quick-sheet][open]').forEach((dialog) => {
    if (dialog instanceof HTMLDialogElement) dialog.close();
  });
}

/* ----------------------------------------------------------------------------------------------
 * Wiring
 * ------------------------------------------------------------------------------------------- */

/** @param {MouseEvent} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const size = target.closest('[data-jj-size]');
  if (size instanceof HTMLElement) {
    selectSize(size);
    return;
  }

  const add = target.closest('[data-jj-quick-add]');
  if (add instanceof HTMLElement) {
    quickAdd(add);
    return;
  }

  const open = target.closest('[data-jj-quick-open]');
  if (open instanceof HTMLElement) {
    openSheet(open);
    return;
  }

  const sheetClose = target.closest('[data-jj-quick-close]');
  if (sheetClose) {
    const sheet = sheetClose.closest('dialog');
    if (sheet instanceof HTMLDialogElement) sheet.close();
    return;
  }

  // Click on the backdrop of the sheet (outside its box) closes it
  if (target instanceof HTMLDialogElement && target.matches('[data-jj-quick-sheet]')) {
    const box = target.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) target.close();
  }
}

document.addEventListener('click', onClick);
document.addEventListener('close', onDialogClose, true);
desktopQuery.addEventListener('change', onBreakpointChange);
